import alloSquare from '@/assets/images/icon.png';
import { ScopeGroupCard } from '@/components/oauth/scope-group-card';
import { Button } from '@/components/ui/design-system';
import { matchScopeGroups } from '@/constants/oauth-scopes';
import { useTranslation } from '@/hooks/use-translation';
import { saveOAuthConsentReturn } from '@/lib/oauth-consent-return';
import { cn } from '@/lib/utils';
import { $fetchAllo } from '@/services/api-allo/client';
import { supabase } from '@/services/supabase';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, ArrowRight, ChevronDown, Globe, Loader2, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

const consentSearchSchema = z.object({
  client_id: fallback(z.string().optional(), undefined),
  scope: fallback(z.string(), ''),
  state: fallback(z.string(), ''),
  redirect_uri: fallback(z.string().optional(), undefined),
  code_challenge: fallback(z.string().optional(), undefined),
  code_challenge_method: fallback(z.string().optional(), undefined),
});

type AppInfo = {
  name?: string;
  logo_url?: string | null;
  website_url?: string | null;
  scopes?: string[];
};

const DEFAULT_EXPANDED_COUNT = 3;

const OAuthConsentPage = () => {
  const { t } = useTranslation('desktop_ui');
  const reduceMotion = useReducedMotion();
  const { client_id, scope, state, redirect_uri, code_challenge, code_challenge_method } =
    Route.useSearch();

  const [appInfo, setAppInfo] = useState<AppInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const requestedScopes = useMemo(() => scope.split(' ').filter(Boolean), [scope]);
  const { matched: matchedGroups, unmatched: unknownScopes } = useMemo(
    () => matchScopeGroups(requestedScopes),
    [requestedScopes],
  );

  // If the params are clearly wrong, fail fast before any network call.
  const paramError = !client_id || !redirect_uri ? 'oauth_consent.error.invalid_redirect' : null;

  useEffect(() => {
    if (paramError || !client_id) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      try {
        const [appRes, sessionRes] = await Promise.all([
          $fetchAllo.GET('/v1/oauth/app-info', {
            params: { query: { client_id } },
          }),
          supabase.auth.getSession(),
        ]);

        if (appRes.data) {
          setAppInfo(appRes.data);
        } else {
          setError('oauth_consent.error.unknown_app');
        }
        setUserEmail(sessionRes.data.session?.user?.email ?? null);
      } catch {
        setError('oauth_consent.error.unknown_app');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [client_id, paramError]);

  const handleAuthorize = useCallback(async () => {
    if (!client_id || !redirect_uri) {
      setError('oauth_consent.error.invalid_redirect');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await $fetchAllo.POST('/v1/oauth/authorize', {
        body: {
          client_id,
          scope,
          state,
          redirect_uri,
          code_challenge: code_challenge ?? undefined,
          code_challenge_method: code_challenge_method ?? undefined,
        },
      });

      const data = res.data as { redirect_url?: string } | undefined;
      if (data?.redirect_url) {
        window.location.replace(data.redirect_url);
        return;
      }
      setError('oauth_consent.error.submit_failed');
      setSubmitting(false);
    } catch {
      setError('oauth_consent.error.submit_failed');
      setSubmitting(false);
    }
  }, [client_id, scope, state, redirect_uri, code_challenge, code_challenge_method]);

  const handleCancel = useCallback(() => {
    if (!redirect_uri) {
      return;
    }
    const separator = redirect_uri.includes('?') ? '&' : '?';
    const url = `${redirect_uri}${separator}error=access_denied&state=${encodeURIComponent(state)}`;
    window.location.replace(url);
  }, [redirect_uri, state]);

  const appName = appInfo?.name ?? t('oauth_consent.fallback_app_name');

  const groupsToRender = showAll ? matchedGroups : matchedGroups.slice(0, DEFAULT_EXPANDED_COUNT);
  const hiddenGroupCount = Math.max(matchedGroups.length - DEFAULT_EXPANDED_COUNT, 0);

  const motionProps = reduceMotion
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.32, ease: 'easeOut' as const },
      };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-primary-3 p-4">
      <motion.div
        {...motionProps}
        className={cn(
          'flex w-full max-w-[480px] flex-col overflow-hidden rounded-2xl border border-primary-10 bg-white-100 shadow-sm',
          'max-h-[min(720px,calc(100dvh-2rem))]',
        )}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-2 px-8 py-16">
            <Loader2 className="size-5 animate-spin text-primary-50" />
            <p className="text-body-13 text-primary-70">{t('oauth_consent.loading')}</p>
          </div>
        ) : paramError ? (
          <ConsentErrorState
            titleKey="oauth_consent.error.invalid_redirect_title"
            descriptionKey="oauth_consent.error.invalid_redirect"
          />
        ) : error && !appInfo ? (
          <ConsentErrorState
            titleKey="oauth_consent.error.unknown_app_title"
            descriptionKey={error}
          />
        ) : appInfo ? (
          <>
            {/* HEADER */}
            <header className="flex shrink-0 flex-col items-center gap-4 px-6 pt-8 pb-5">
              <LogoBridge appName={appName} appLogoUrl={appInfo.logo_url ?? null} />
              <div className="flex flex-col items-center gap-3">
                <h1 className="text-center text-heading-20 text-primary-100">
                  {t('oauth_consent.title_prefix')}{' '}
                  <span className="text-highlight">{appName}</span>{' '}
                  {t('oauth_consent.title_suffix')}
                </h1>
                {userEmail ? (
                  <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary-10 bg-primary-5 px-3 py-1.5">
                    <ShieldCheck
                      className="size-3.5 shrink-0 text-primary-70"
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    <span className="truncate text-body-13 text-primary-70">
                      {t('oauth_consent.signed_in_as')}{' '}
                      <span className="text-primary-100">{userEmail}</span>
                    </span>
                  </div>
                ) : null}
              </div>
            </header>

            <div className="shrink-0 border-t border-primary-5" />

            {/* BODY */}
            <div
              className={cn(
                'flex-1 overflow-y-auto px-6 pb-4',
                '[mask-image:linear-gradient(to_bottom,black_calc(100%-24px),transparent)]',
              )}
            >
              <p className="pt-4 pb-2 text-body-12 text-primary-70">
                {t('oauth_consent.section_label', { appName })}
              </p>

              {matchedGroups.length === 0 && unknownScopes.length === 0 ? (
                <p className="rounded-xl bg-primary-5 px-4 py-3 text-body-13 text-primary-70">
                  {t('oauth_consent.no_scopes')}
                </p>
              ) : (
                <div className="divide-y divide-primary-5">
                  {groupsToRender.map((group) => (
                    <ScopeGroupCard
                      key={group.id}
                      icon={group.icon}
                      titleKey={group.title}
                      descriptionKey={group.description}
                      destructive={group.destructive}
                    />
                  ))}
                  {showAll && unknownScopes.length > 0 ? (
                    <div className="px-1 py-2">
                      <p className="text-body-13-bold text-primary-100">
                        {t('oauth_consent.scope.other.title', { count: unknownScopes.length })}
                      </p>
                      <p className="mt-0.5 text-body-13 text-primary-70">
                        {unknownScopes.join(', ')}
                      </p>
                    </div>
                  ) : null}
                </div>
              )}

              {hiddenGroupCount > 0 || (unknownScopes.length > 0 && !showAll) ? (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-2 inline-flex items-center gap-1 rounded-md px-1 py-1 text-body-13 text-highlight transition-colors hover:underline"
                  aria-expanded={showAll}
                >
                  {showAll
                    ? t('oauth_consent.show_less')
                    : t('oauth_consent.show_all', {
                        count: hiddenGroupCount + (unknownScopes.length > 0 ? 1 : 0),
                      })}
                  <ChevronDown
                    className={cn('size-3.5 transition-transform', showAll && 'rotate-180')}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </button>
              ) : null}
            </div>

            {/* FOOTER */}
            <footer className="sticky bottom-0 z-10 shrink-0 border-t border-primary-5 bg-white-100 px-6 pt-4 pb-5">
              <AnimatePresence>
                {error && appInfo ? (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-3 flex items-start gap-2 rounded-lg bg-status-danger-light px-3 py-2 text-body-13 text-status-danger"
                    role="alert"
                  >
                    <AlertCircle className="mt-px size-4 shrink-0" aria-hidden="true" />
                    <span>{t(error)}</span>
                  </motion.div>
                ) : null}
              </AnimatePresence>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleCancel}
                  disabled={submitting}
                  className="flex-1"
                >
                  {t('oauth_consent.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleAuthorize}
                  isLoading={submitting}
                  disabled={submitting || !!paramError}
                  className="flex-1"
                >
                  {t('oauth_consent.authorize')}
                </Button>
              </div>
            </footer>
          </>
        ) : null}
      </motion.div>
    </div>
  );
};

type LogoBridgeProps = {
  appName: string;
  appLogoUrl: string | null;
};

const LogoBridge = ({ appName, appLogoUrl }: LogoBridgeProps) => (
  <div className="flex items-center gap-2">
    <img src={alloSquare} alt="Allo" className="size-12 object-contain" />
    <div className="flex items-center gap-1.5" aria-hidden="true">
      <span className="block h-px w-5 border-t border-dashed border-primary-20" />
      <ArrowRight className="size-4 text-primary-50" strokeWidth={2} aria-hidden="true" />
      <span className="block h-px w-5 border-t border-dashed border-primary-20" />
    </div>
    {appLogoUrl ? (
      <img src={appLogoUrl} alt={appName} className="size-12 object-contain" />
    ) : (
      <Globe className="size-12 text-primary-50" strokeWidth={1.5} aria-hidden="true" />
    )}
  </div>
);

type ConsentErrorStateProps = {
  titleKey: string;
  descriptionKey: string;
};

const ConsentErrorState = ({ titleKey, descriptionKey }: ConsentErrorStateProps) => {
  const { t } = useTranslation('desktop_ui');
  return (
    <div className="flex flex-col items-center gap-3 px-8 py-12 text-center">
      <span className="flex size-10 items-center justify-center rounded-full bg-status-danger-light text-status-danger">
        <AlertCircle className="size-5" aria-hidden="true" />
      </span>
      <h2 className="text-heading-18 text-primary-100">{t(titleKey)}</h2>
      <p className="text-body-13 text-primary-70">{t(descriptionKey)}</p>
    </div>
  );
};

export const Route = createFileRoute('/oauth/consent')({
  validateSearch: zodValidator(consentSearchSchema),
  beforeLoad: async () => {
    const authSess = await supabase.auth.getSession();
    if (!authSess.data.session) {
      saveOAuthConsentReturn(`${window.location.pathname}${window.location.search}`);
      throw redirect({ to: '/login', replace: true });
    }
  },
  component: OAuthConsentPage,
});
